import { useCallback, useState } from "react";
import { ApiError, type ApiClient } from "@keydials/api-client";
import { useWorkspaceServices } from "../../context";
import type { BringBackWrite, CompareRow } from "./compareRows";
import { fmtCompareDate } from "./compareModel";

/**
 * "Bring back": Then's value copied into Now, as ordinary changes made today by whoever pressed it.
 *
 * Written straight through the API rather than the workspace controller's setters: those report a refusal
 * as a banner and resolve anyway, and a bulk copy has to know exactly which writes landed — to say "2 of 3",
 * to offer "Try again" on the one that didn't, and to undo only what it actually changed. The host is asked
 * to refresh once the sequence is over, so the dial and the card redraw from the server's own answer.
 */

/** One write's outcome. */
export interface BringBackOutcome {
  row: CompareRow;
  ok: boolean;
  /** The server's own reason, on a refusal. */
  reason?: string;
}

/**
 * Sends the writes one after another — never in parallel, so a status that depends on a field brought back
 * just before it sees that field — and carries on past a refusal: one gate saying no is not a reason to
 * leave the other values where they were. Pure over `write`, so the sequencing is testable on its own.
 */
export async function runSequence(
  rows: CompareRow[],
  write: (w: BringBackWrite) => Promise<void>,
  pick: (row: CompareRow) => BringBackWrite | undefined = (row) => row.bringBack?.write,
): Promise<BringBackOutcome[]> {
  const out: BringBackOutcome[] = [];
  for (const row of rows) {
    const w = pick(row);
    if (!w) continue;
    try {
      await write(w);
      out.push({ row, ok: true });
    } catch (e) {
      out.push({ row, ok: false, reason: e instanceof Error ? e.message : "Refused" });
    }
  }
  return out;
}

/** Undo: the succeeded writes reversed, newest first, each put back to what Now held before. */
export function undoRows(outcomes: BringBackOutcome[]): CompareRow[] {
  return outcomes.filter((o) => o.ok).map((o) => o.row).reverse();
}

/** The one place a BringBackWrite becomes a request. `from` tags it; an undo passes none. */
export function sendWrite(api: ApiClient, token: string, instanceId: string, w: BringBackWrite, from: string | null): Promise<void> {
  const note = from ? `Brought back from ${fmtCompareDate(from)}` : null;
  switch (w.kind) {
    case "segmentStatus":
      return api.changeSegmentStatus(token, instanceId, { sectionKey: w.sectionKey, segmentKey: w.segmentKey, statusKey: w.statusKey, note, reviewDate: null, revertToStatusKey: null });
    case "reviewDate":
      // A review date travels with its status: the node's current status is re-sent with the old date.
      return api.changeSegmentStatus(token, instanceId, {
        sectionKey: w.sectionKey, segmentKey: w.segmentKey, statusKey: w.statusKey, note,
        reviewDate: w.reviewDate, revertToStatusKey: w.reviewDate ? w.revertToStatusKey : null,
      });
    case "itemStatus":
      return api.changeItemStatus(token, instanceId, { sectionKey: w.sectionKey, segmentKey: w.segmentKey, itemKey: w.itemKey, dimensionKey: w.dimensionKey, statusKey: w.statusKey, note, reviewDate: null });
    case "sectionStatus":
      return api.changeSectionStatus(token, instanceId, { sectionKey: w.sectionKey, statusKey: w.statusKey, note });
    case "field":
      return api.setNodeAttribute(token, instanceId, {
        target: w.target, sectionKey: w.sectionKey, attributeKey: w.attributeKey, value: w.value,
        segmentKey: w.segmentKey || null, itemKey: w.itemKey || null,
        broughtBackFrom: from ? new Date(`${from}T12:00:00`).toISOString() : null,
      });
  }
}

/** A refusal in words: the server's own sentence when it sent one. */
function reasonOf(e: unknown): string {
  if (e instanceof ApiError) return e.message || `Refused (${e.status})`;
  return e instanceof Error ? e.message : "Refused";
}

export interface BringBackState {
  busy: boolean;
  /** The last sequence's outcomes, until dismissed or undone. */
  last: BringBackOutcome[] | null;
  bringBack: (rows: CompareRow[]) => Promise<BringBackOutcome[]>;
  undo: () => Promise<void>;
  dismiss: () => void;
}

export function useBringBack(instanceId: string, day: string, onChanged?: () => void): BringBackState {
  const { api, getAccessToken } = useWorkspaceServices();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<BringBackOutcome[] | null>(null);

  const write = useCallback((from: string | null) => async (w: BringBackWrite) => {
    const token = getAccessToken();
    if (!token) throw new Error("Signed out");
    try {
      await sendWrite(api, token, instanceId, w, from);
    } catch (e) {
      throw new Error(reasonOf(e));
    }
  }, [api, getAccessToken, instanceId]);

  const bringBack = useCallback(async (rows: CompareRow[]) => {
    setBusy(true);
    try {
      const out = await runSequence(rows, write(day));
      setLast(out);
      return out;
    } finally {
      setBusy(false);
      onChanged?.();
    }
  }, [write, day, onChanged]);

  const undo = useCallback(async () => {
    if (!last) return;
    setBusy(true);
    try {
      await runSequence(undoRows(last), write(null), (row) => row.bringBack?.undo);
      setLast(null);
    } finally {
      setBusy(false);
      onChanged?.();
    }
  }, [last, write, onChanged]);

  return { busy, last, bringBack, undo, dismiss: useCallback(() => setLast(null), []) };
}
